import { DestroyRef, Injectable, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Subject } from 'rxjs';
import { FileManagerService } from './file-manager.service';
import { FileBrowserStore } from './file-browser.store';
import { UiStateService } from './ui-state.service';
import { MessageService } from 'primeng/api';
import type { FileItem } from '../models';


@Injectable()
export class FileMoveStore {
  private readonly api = inject(FileManagerService);
  private readonly browserStore = inject(FileBrowserStore);
  private readonly uiState = inject(UiStateService);
  private readonly messageService = inject(MessageService);
  private readonly destroyRef = inject(DestroyRef);

  readonly moved = new Subject<string>();

  canAcceptMove(panelId: string): boolean {
    const source = this.uiState.dragSource();
    return !!source && source.panelId !== panelId && !this.browserStore.readOnly();
  }

  drop(json: string, panelId: string, currentPath: string): void {
    const source = this.uiState.dragSource();
    if (!source || source.panelId === panelId || this.browserStore.readOnly()) return;

    let files: FileItem[] = [];
    try {
      files = JSON.parse(json);
    } catch (e) {
      console.error('Error parsing dropped files data:', e);
      return;
    }

    const candidates = (Array.isArray(files) ? files : [])
      .map((f) => f.apiPath)
      .filter((p): p is string => !!p);

    if (candidates.some((p) => p === currentPath || currentPath.startsWith(`${p}/`))) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Érvénytelen áthelyezés',
        detail: 'Egy mappát nem lehet önmagába vagy egy almappájába helyezni.',
        life: 6000,
      });
      return;
    }

    const sources = candidates.filter((p) => this.parentDirOf(p) !== currentPath);
    if (sources.length === 0) return;

    this.browserStore.setLoading(true);
    this.api
      .moveFiles(source.path, sources, currentPath)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => this.moved.next(source.panelId),
        error: (err) => {
          console.error('Move failed:', err);
          this.browserStore.setLoading(false);
        },
      });
  }

  private parentDirOf(path: string): string {
    const idx = path.lastIndexOf('/');
    const base = path.substring(0, idx);
    return base.endsWith(':/') ? `${base}/` : base;
  }
}
